import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { LoadingState } from '@/components/Spinner';

interface Client {
  id: string; name: string; owner_coach_id: string; coach_name: string; feedbacker_count: number;
  information_disclosure_agreement_uploaded?: boolean;
}
interface Feedbacker { id: string; name: string; email: string }

export function ClientListPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [feedbackers, setFeedbackers] = useState<Feedbacker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [selectedFBs, setSelectedFBs] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);

  const fetchClients = useCallback(() => {
    setLoading(true);
    api.get('/clients').then(r => { setClients(r.data); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const openForm = async () => {
    try {
      const resp = await api.get('/admin/feedbackers');
      setFeedbackers(resp.data);
    } catch {
      setFeedbackers([]);
    }
    setShowForm(true);
  };

  const toggleFB = (id: string) => {
    setSelectedFBs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await api.post('/clients', { name: newName.trim(), feedbacker_ids: Array.from(selectedFBs) });
      setShowForm(false);
      setNewName('');
      setSelectedFBs(new Set());
      fetchClients();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string | { message?: string }; message?: string } } }).response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : detail?.message ?? (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      alert(msg || 'クライアントの追加に失敗しました');
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <div className="p-8 flex justify-center"><LoadingState /></div>;
  if (error) return <div className="p-8 text-center text-red-500">{error}</div>;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">クライアント一覧</h1>
        <button type="button" onClick={openForm} className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">+ クライアント追加</button>
      </div>

      {showForm && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
          <h3 className="text-sm font-medium text-blue-900">新規クライアント追加</h3>
          <div className="mt-2 space-y-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">クライアント略称</label>
              <input type="text" placeholder="例: ケイタスプラン" value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreate()}
                className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">フィードバッカーを選択（複数可）</label>
              <div className="max-h-40 overflow-y-auto rounded border border-gray-200 bg-white p-2 space-y-1">
                {feedbackers.length === 0 ? (
                  <p className="text-xs text-gray-400">フィードバッカー権限を持つユーザーがいません</p>
                ) : feedbackers.map(fb => (
                  <label key={fb.id} className="flex items-center gap-2 rounded px-2 py-1 hover:bg-gray-50 cursor-pointer">
                    <input type="checkbox" checked={selectedFBs.has(fb.id)} onChange={() => toggleFB(fb.id)} />
                    <span className="text-sm">{fb.name}</span>
                    <span className="text-xs text-gray-400">{fb.email}</span>
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-400">
                デフォルトフィードバッカー（あなたの直属上司）は自動で追加されます
              </p>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={handleCreate} disabled={creating || !newName.trim()}
                className="rounded bg-blue-600 px-4 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50">
                {creating ? '追加中...' : '追加'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setNewName(''); setSelectedFBs(new Set()); }}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-600">キャンセル</button>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {clients.map(c => (
          <div key={c.id} onClick={() => navigate(`/clients/${c.id}`)}
            className="cursor-pointer rounded-lg bg-white p-5 shadow-sm hover:shadow-md transition-shadow">
            <h3 className="text-lg font-semibold text-gray-900">{c.name}</h3>
            <div className="mt-3 space-y-1 text-sm text-gray-600">
              <p>担当コーチ: {c.coach_name}</p>
              <p>フィードバッカー: {c.feedbacker_count} 名</p>
              <p>
                合意書PDF:{' '}
                <span className={c.information_disclosure_agreement_uploaded ? 'text-green-700' : 'text-red-600'}>
                  {c.information_disclosure_agreement_uploaded ? 'アップロード済み' : '未アップロード'}
                </span>
              </p>
            </div>
          </div>
        ))}
        {clients.length === 0 && <p className="text-gray-400">クライアントがありません</p>}
      </div>
    </div>
  );
}
